import { config } from './config';

export interface ChatMessage {
  timestamp: string;
  isUser: boolean;
  text: string;
}

export interface UserQueryRequest {
  query: string;
  chat?: ChatMessage[];
  userUID: string;
}

export interface UserQueryResponse {
  response: string;
}

export interface FeedbackRequest {
  userID: string;
  messageID: string;
  feedback: 'helpful' | 'not_helpful';
  query: string;
  answer: string;
  timestamp: string;
}

class APIService {
  private baseURL = config.API_BASE;

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async sendUserQuery(request: UserQueryRequest, token: string): Promise<UserQueryResponse> {
    return this.makeRequest<UserQueryResponse>('/userquery', {
      method: 'POST',
      body: JSON.stringify(request),
    }, token);
  }

  async sendFeedback(request: FeedbackRequest): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>('/feedback', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async exchangeGoogleToken(authCode: string, userId: string): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>('/googletokenexchange', {
      method: 'POST',
      body: JSON.stringify({
        auth_code: authCode,
        user_id: userId,
      }),
    });
  }

  async storeFirefliesKey(userId: string, apiKey: string): Promise<{ success: boolean }> {
    return this.makeRequest<{ success: boolean }>('/store_fireflies_key', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        api_key: apiKey,
      }),
    });
  }

  async trackLocation(locationData: any): Promise<{ status: string; message: string }> {
    return this.makeRequest<{ status: string; message: string }>('/locationchanges', {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  }

  async savePersonalGoals(goals: {
    eating: number;
    sleeping: number;
    exercising: number;
    work: number;
  }): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>('/personalgoals', {
      method: 'POST',
      body: JSON.stringify(goals),
    });
  }

  async saveOnboardingInterview(userId: string, responses: Record<string, any>): Promise<{ status: string; message: string }> {
    return this.makeRequest<{ status: string; message: string }>('/onboardingInterview', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        responses,
      }),
    });
  }
}

export const apiService = new APIService();